import { isDate } from '@/lib/utils/date';
import { getRecordValue, isPlainObject } from '@/lib/utils/type-guards';

const isPlainRecord = (value: unknown): value is Record<string, unknown> => isPlainObject(value);

import type {
  AnalyzeTrendsActiveUserData,
  AnalyzeTrendsPopularTweetData,
  AnalyzeTrendsTimeRange,
  AnalyzeTrendsToolResultData,
  ArchiveDataset,
  ArchiveLimitMetadataData,
  ArchiveTweetSummaryData,
  ArchiveTweetToolResultData,
  CompareEngagementTimeMetric,
  CompareEngagementToolResultData,
  CompareEngagementUserMetric,
  SearchTimelineToolResultData,
  SearchTimelineTweetData,
  TweetToolResultData,
  UserProfileToolResultData,
  VerifiedType,
} from './types';

const hasErrorField = (value: Record<string, unknown>): value is Record<string, unknown> & { error: unknown } =>
  'error' in value;

export const isStringOrNull = (value: unknown): value is string | null | undefined =>
  typeof value === 'string' || value === null || value === undefined;

export const isNumberOrNull = (value: unknown): value is number | null | undefined =>
  typeof value === 'number' || value === null || value === undefined;

export const isBooleanOrNull = (value: unknown): value is boolean | null | undefined =>
  typeof value === 'boolean' || value === null || value === undefined;

export const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isTimelinePaginationPayloadLike = (
  value: unknown
): value is Record<string, unknown> & {
  cursor: string | null;
  hasMore: boolean;
} => {
  if (!isPlainRecord(value)) {
    return false;
  }
  const cursor = getRecordValue(value, 'cursor');
  if (!(cursor === null || cursor === undefined || typeof cursor === 'string')) {
    return false;
  }
  const hasMore = getRecordValue(value, 'hasMore');
  if (typeof hasMore !== 'boolean') {
    return false;
  }
  const limit = getRecordValue(value, 'limit');
  if (!(limit === null || limit === undefined || (typeof limit === 'number' && Number.isFinite(limit)))) {
    return false;
  }
  const offset = getRecordValue(value, 'offset');
  if (!(offset === null || offset === undefined || (typeof offset === 'number' && Number.isFinite(offset)))) {
    return false;
  }
  const totalCount = getRecordValue(value, 'totalCount');
  if (
    !(
      totalCount === null ||
      totalCount === undefined ||
      (typeof totalCount === 'number' && Number.isFinite(totalCount))
    )
  ) {
    return false;
  }
  return true;
};

const isDateLike = (value: unknown): value is Date | string | null | undefined => {
  if (value === null || value === undefined) {
    return true;
  }
  if (isDate(value)) {
    return !Number.isNaN(value.valueOf());
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed);
  }
  return false;
};

export const isVerifiedType = (value: unknown): value is VerifiedType =>
  value === 'none' || value === 'blue' || value === 'business' || value === 'government';

export const isTweetToolResult = (value: unknown): value is TweetToolResultData => {
  if (!isPlainObject(value) || hasErrorField(value)) {
    return false;
  }
  const record = value as Partial<TweetToolResultData>;
  if (typeof record.id !== 'string') {
    return false;
  }
  const result =
    isStringOrNull(record.text) &&
    isStringOrNull(record.author) &&
    isStringOrNull(record.authorName) &&
    isNumberOrNull(record.likes) &&
    isNumberOrNull(record.retweets) &&
    isNumberOrNull(record.replies) &&
    isDateLike(record.createdAt) &&
    (record.verifiedType === undefined || isVerifiedType(record.verifiedType));
  return result;
};

export const isUserProfileToolResult = (value: unknown): value is UserProfileToolResultData => {
  if (!isPlainObject(value) || hasErrorField(value)) {
    return false;
  }
  const record = value as Partial<UserProfileToolResultData>;
  if (typeof record.username !== 'string') {
    return false;
  }
  return (
    isStringOrNull(record.name) &&
    isStringOrNull(record.bio) &&
    isNumberOrNull(record.followers) &&
    isNumberOrNull(record.following) &&
    isNumberOrNull(record.tweets) &&
    isBooleanOrNull(record.verified) &&
    isDateLike(record.joined) &&
    (record.verifiedType === undefined || isVerifiedType(record.verifiedType))
  );
};

export const isAnalyzeTrendsTimeRange = (value: unknown): value is AnalyzeTrendsTimeRange => {
  if (typeof value !== 'string') {
    return false;
  }
  return value === '6h' || value === '12h' || value === '24h' || value === '3d' || value === '7d';
};

const isAnalyzeTrendTweet = (value: unknown): value is AnalyzeTrendsPopularTweetData => {
  if (!isPlainObject(value)) {
    return false;
  }
  const record = value as Partial<AnalyzeTrendsPopularTweetData>;
  return (
    typeof record.id === 'string' &&
    typeof record.text === 'string' &&
    isStringOrNull(record.authorHandle) &&
    isStringOrNull(record.authorName) &&
    isNumberOrNull(record.likes) &&
    isNumberOrNull(record.retweets) &&
    isNumberOrNull(record.replies) &&
    (record.verifiedType === undefined || isVerifiedType(record.verifiedType)) &&
    isDateLike(record.createdAt)
  );
};

const isAnalyzeTrendUser = (value: unknown): value is AnalyzeTrendsActiveUserData => {
  if (!isPlainObject(value)) {
    return false;
  }
  const record = value as Partial<AnalyzeTrendsActiveUserData>;
  return (
    typeof record.username === 'string' &&
    typeof record.name === 'string' &&
    isNumber(record.posts) &&
    isNumber(record.totalLikes)
  );
};

export const isAnalyzeTrendsToolResult = (value: unknown): value is AnalyzeTrendsToolResultData => {
  if (!isPlainObject(value) || hasErrorField(value)) {
    return false;
  }
  const record = value as Partial<AnalyzeTrendsToolResultData>;
  if (!isAnalyzeTrendsTimeRange(record.timeRange)) {
    return false;
  }
  if (!Array.isArray(record.popularTweets)) {
    return false;
  }
  if (!record.popularTweets.every((tweet): tweet is AnalyzeTrendsPopularTweetData => isAnalyzeTrendTweet(tweet))) {
    return false;
  }
  if (!Array.isArray(record.activeUsers)) {
    return false;
  }
  if (!record.activeUsers.every((user): user is AnalyzeTrendsActiveUserData => isAnalyzeTrendUser(user))) {
    return false;
  }
  return true;
};

const isCompareEngagementUserMetric = (value: unknown): value is CompareEngagementUserMetric => {
  if (!isPlainObject(value)) {
    return false;
  }
  const record = value as Partial<CompareEngagementUserMetric>;
  return (
    typeof record.username === 'string' &&
    isNumber(record.avgLikes) &&
    isNumber(record.avgRetweets) &&
    isNumber(record.totalTweets)
  );
};

const isCompareEngagementTimeMetric = (value: unknown): value is CompareEngagementTimeMetric => {
  if (!isPlainObject(value)) {
    return false;
  }
  const record = value as Partial<CompareEngagementTimeMetric>;
  return (
    typeof record.period === 'string' &&
    isNumber(record.avgLikes) &&
    isNumber(record.avgRetweets) &&
    isNumber(record.totalTweets)
  );
};

export const isCompareEngagementToolResult = (value: unknown): value is CompareEngagementToolResultData => {
  if (!isPlainObject(value) || hasErrorField(value)) {
    return false;
  }
  const record = value as Partial<CompareEngagementToolResultData>;
  const type = record.type;
  if (type === 'users') {
    const metrics = record.metrics;
    return (
      Array.isArray(metrics) &&
      metrics.every((metric): metric is CompareEngagementUserMetric => isCompareEngagementUserMetric(metric))
    );
  }
  if (type === 'time') {
    const metrics = record.metrics;
    return (
      Array.isArray(metrics) &&
      metrics.every((metric): metric is CompareEngagementTimeMetric => isCompareEngagementTimeMetric(metric))
    );
  }
  return false;
};

const isSearchTimelineTweet = (value: unknown): value is SearchTimelineTweetData => {
  if (!isPlainObject(value)) {
    return false;
  }
  const record = value as Partial<SearchTimelineTweetData>;
  return (
    typeof record.id === 'string' &&
    isStringOrNull(record.text) &&
    isStringOrNull(record.author) &&
    isNumberOrNull(record.likes) &&
    isNumberOrNull(record.retweets)
  );
};

export const isSearchTimelineToolResult = (value: unknown): value is SearchTimelineToolResultData => {
  if (!isPlainObject(value) || hasErrorField(value)) {
    return false;
  }
  const record = value as Partial<SearchTimelineToolResultData>;
  if (typeof record.found !== 'number') {
    return false;
  }
  if (!Array.isArray(record.tweets)) {
    return false;
  }
  if (!record.tweets.every((tweet): tweet is SearchTimelineTweetData => isSearchTimelineTweet(tweet))) {
    return false;
  }
  return true;
};

export const isArchiveDataset = (value: unknown): value is ArchiveDataset =>
  value === 'tweets' || value === 'likes' || value === 'bookmarks';

const isArchiveTweetMetrics = (value: unknown): value is ArchiveTweetSummaryData['metrics'] => {
  if (!isPlainObject(value)) {
    return false;
  }
  return (
    isNumberOrNull((value as { likes?: unknown }).likes) &&
    isNumberOrNull((value as { retweets?: unknown }).retweets) &&
    isNumberOrNull((value as { replies?: unknown }).replies) &&
    isNumberOrNull((value as { quotes?: unknown }).quotes) &&
    isNumberOrNull((value as { bookmarks?: unknown }).bookmarks) &&
    isNumberOrNull((value as { impressions?: unknown }).impressions)
  );
};

const isArchiveTweetAuthor = (value: unknown): value is NonNullable<ArchiveTweetSummaryData['author']> => {
  if (!isPlainObject(value)) {
    return false;
  }
  return (
    isStringOrNull((value as { id?: unknown }).id) &&
    isStringOrNull((value as { username?: unknown }).username) &&
    isStringOrNull((value as { name?: unknown }).name) &&
    isStringOrNull((value as { profileImageUrl?: unknown }).profileImageUrl) &&
    isVerifiedType((value as { verifiedType?: unknown }).verifiedType)
  );
};

export const isArchiveTweetSummary = (value: unknown): value is ArchiveTweetSummaryData => {
  if (!isPlainObject(value)) {
    return false;
  }
  if (typeof (value as { id?: unknown }).id !== 'string') {
    return false;
  }
  if (typeof (value as { text?: unknown }).text !== 'string') {
    return false;
  }
  if (!isArchiveDataset((value as { dataset?: unknown }).dataset)) {
    return false;
  }
  if (!isDateLike((value as { createdAt?: unknown }).createdAt)) {
    return false;
  }
  if (!isDateLike((value as { sourceTimestamp?: unknown }).sourceTimestamp)) {
    return false;
  }
  const author = (value as { author?: unknown }).author;
  if (!(author === null || isArchiveTweetAuthor(author))) {
    return false;
  }
  if (!isArchiveTweetMetrics((value as { metrics?: unknown }).metrics)) {
    return false;
  }
  return true;
};

const isArchiveLimitMetadata = (value: unknown): value is ArchiveLimitMetadataData => {
  if (!isPlainObject(value)) {
    return false;
  }
  const requested = (value as { requested?: unknown }).requested;
  if (
    !(requested === null || requested === undefined || (typeof requested === 'number' && Number.isFinite(requested)))
  ) {
    return false;
  }
  const applied = (value as { applied?: unknown }).applied;
  const max = (value as { max?: unknown }).max;
  const defaultLimit = (value as { default?: unknown }).default;
  const recommendedNext = (value as { recommendedNext?: unknown }).recommendedNext;
  return [applied, max, defaultLimit, recommendedNext].every(
    (entry) => typeof entry === 'number' && Number.isFinite(entry) && entry >= 0
  );
};

export const isArchiveTweetResult = (value: unknown): value is ArchiveTweetToolResultData => {
  if (!isPlainRecord(value) || hasErrorField(value)) {
    return false;
  }
  const dataset = getRecordValue(value, 'dataset');
  if (!isArchiveDataset(dataset)) {
    return false;
  }
  const items = getRecordValue(value, 'items');
  if (!Array.isArray(items)) {
    return false;
  }
  if (!items.every((item): item is ArchiveTweetSummaryData => isArchiveTweetSummary(item))) {
    return false;
  }
  const pagination = getRecordValue(value, 'pagination');
  if (!isTimelinePaginationPayloadLike(pagination)) {
    return false;
  }
  const limits = getRecordValue(value, 'limits');
  if (!isArchiveLimitMetadata(limits)) {
    return false;
  }
  return true;
};
